// Clavian Boot on the Floor: how each network-started PC stands, on its tile and in the station window.
//
// `useFloorBoot` reads `GET …/boot/floor` once per venue for a member holding `boot:view` — the full summary, which the
// station window's Windows & games tab needs — and, for every other member who sees the Floor (a cashier), the simple
// states (`GET …/boot/floor-states`: the state and why it cannot start, nothing else). An organisation without the Clavian
// Boot add-on answers `402` once and is not asked again. Both are keyed by station, so a tile finds its PC's state with no
// read of its own. A venue without Clavian Boot gets empty maps and the Floor is unchanged.
//
// `BootStrip` is the tile's strip: a dot and a few words ("Ready", "Update waiting", "Can't start" with why), never a
// coloured pill, and a status always comes with words.

import type { BootFloorPc, BootFloorProblem } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { cx } from '@clavian/ui-web';
import { useMemo, type ReactNode } from 'react';

import type { Access } from '../api/queries';
import { useNow } from '../floor/live';
import { Icon } from '../shell/nav-icons';
import { useBootFloor, useBootFloorStates, type BootScope } from './queries';
import { useBootLive } from './realtime';

type ConsoleT = ReturnType<typeof useTranslation>['t'];

export const BOOT_VIEW_PERMISSION = 'boot:view';
const FLOOR_READ_PERMISSION = 'floor:read';

/**
 * What a tile needs of a PC's boot state: the state, why it cannot start, and — with `boot:view` — whether Windows is being
 * changed on it. A member who sees the Floor without `boot:view` gets exactly this and nothing more.
 */
export interface BootTileState {
  readonly stationId: string;
  readonly state: BootFloorPc['state'];
  readonly problem?: BootFloorProblem | undefined;
  readonly editing?: boolean;
  /** When a live state (starting, loading Windows, can't start) began: how long a PC has been starting is said from it. */
  readonly stateSince?: string | null | undefined;
}

/** The strip's tone: green ready, warn for an update waiting, danger when it cannot start, neutral otherwise. */
export type BootStripTone = 'ok' | 'warn' | 'danger' | 'info' | 'neutral';

export interface BootStripWords {
  readonly tone: BootStripTone;
  /** The state, in a few words: "Ready", "Update waiting", "Can't start". */
  readonly words: string;
  /** Why it cannot start, in a sentence ("The boot server is offline"); null otherwise. */
  readonly reason: string | null;
  readonly icon: string | null;
}

/** How long ago, in a few characters: "2 min", "3 h"; null under a minute (nothing is said of a PC that has just begun). */
export function durationWords(sinceIso: string | null | undefined, now: number, t: ConsoleT): string | null {
  if (sinceIso == null) return null;
  const minutes = Math.floor((now - Date.parse(sinceIso)) / 60_000);
  if (!Number.isFinite(minutes) || minutes < 1) return null;
  return minutes < 60 ? t('boot.floor.duration.minutes', { count: minutes }) : t('boot.floor.duration.hours', { count: Math.floor(minutes / 60) });
}

/**
 * The words and tone a PC's boot state draws with. An open change of Windows on the PC is said over its state. `unknown`
 * (the boot server went quiet) is never shown as the last state it had: it says it does not know, and why. A PC that has been
 * starting for a minute or more says for how long, when `now` is given.
 */
export function bootStripOf(pc: Pick<BootTileState, 'state' | 'problem' | 'editing'> & { readonly stateSince?: string | null | undefined }, t: ConsoleT, now?: number): BootStripWords {
  if (pc.state === 'unknown') {
    return { tone: 'neutral', words: t('boot.floor.state.unknown'), reason: t('boot.floor.unknownReason'), icon: 'offline' };
  }
  if (pc.state === 'cant_start') {
    return {
      tone: 'danger',
      words: t('boot.floor.state.cant_start'),
      reason: pc.problem === undefined ? null : t(`boot.floor.problem.${pc.problem}`),
      icon: 'alert',
    };
  }
  if (pc.editing === true) return { tone: 'info', words: t('boot.floor.editing'), reason: null, icon: 'wrench' };
  switch (pc.state) {
    case 'ready':
      return { tone: 'ok', words: t('boot.floor.state.ready'), reason: null, icon: null };
    case 'update_waiting':
      return { tone: 'warn', words: t('boot.floor.state.update_waiting'), reason: null, icon: 'download' };
    case 'starting':
    case 'loading_windows': {
      const words = t(`boot.floor.state.${pc.state}`);
      const duration = now === undefined ? null : durationWords(pc.stateSince, now, t);
      return { tone: 'info', words: duration === null ? words : t('boot.floor.since', { words, duration }), reason: null, icon: 'refresh' };
    }
    default:
      return { tone: 'neutral', words: t('boot.floor.state.never_started'), reason: null, icon: null };
  }
}

/** The PC has an update waiting and can take it: it is behind and nothing stops it starting. */
export function hasUpdateWaiting(pc: Pick<BootTileState, 'state'>): boolean {
  return pc.state === 'update_waiting';
}

/** The tile's boot strip: a dot (or an icon) and the state, with why it cannot start under it. */
export function BootStrip({ pc }: { readonly pc: BootTileState }): ReactNode {
  const { t } = useTranslation('console');
  const strip = bootStripOf(pc, t, useNow(30_000));
  return (
    <span className={cx('console-tile__boot', `console-tile__boot--${strip.tone}`)} title={strip.reason ?? strip.words} data-boot-state={pc.state}>
      <span className="console-tile__boot-line">
        {strip.icon === null ? <span className="console-tile__dot" /> : <Icon name={strip.icon} />}
        <span className="console-tile__boot-words">{strip.words}</span>
      </span>
      {strip.reason === null ? null : <span className="console-tile__boot-reason">{strip.reason}</span>}
    </span>
  );
}

/** What a tile's screen reader name adds for a network-started PC: "Starts from the network: Update waiting". */
export function bootAria(pc: BootTileState, t: ConsoleT): string {
  const strip = bootStripOf(pc, t, Date.now());
  return t('boot.floor.stripAria', { state: strip.reason === null ? strip.words : `${strip.words}, ${strip.reason}` });
}

export interface FloorBoot {
  /** Each network-started PC on the floor with its full summary, by its station. Empty without Clavian Boot or `boot:view`. */
  readonly byStation: ReadonlyMap<string, BootFloorPc>;
  /**
   * Each network-started PC's state for its tile, by its station: the full summary with `boot:view`, else the simple state
   * for any member who sees the Floor. Empty without Clavian Boot.
   */
  readonly tiles: ReadonlyMap<string, BootTileState>;
}

const EMPTY: ReadonlyMap<string, BootFloorPc> = new Map();
const EMPTY_TILES: ReadonlyMap<string, BootTileState> = new Map();

/**
 * The venue's boot state on the Floor, kept live over `venue:{v}:boot` while the socket is up (with `boot:view`), or by
 * `floor.boot.changed` on the floor channel (without it).
 */
export function useFloorBoot(scope: BootScope, access: Access): FloorBoot {
  const ready = access.status === 'ready';
  const full = ready && access.can(BOOT_VIEW_PERMISSION);
  // Floor staff see the simple state only where the venue has Clavian Boot: a venue without it is never asked.
  const simple = ready && !full && access.products.boot && access.can(FLOOR_READ_PERMISSION);
  const floor = useBootFloor(scope, full);
  const states = useBootFloorStates(scope, simple);
  useBootLive(scope, full && floor.isSuccess);
  const byStation = useMemo(() => (floor.data === undefined ? EMPTY : new Map(floor.data.map((pc) => [pc.stationId, pc] as const))), [floor.data]);
  const simpleTiles = useMemo(() => (states.data === undefined ? EMPTY_TILES : new Map(states.data.map((pc) => [pc.stationId, pc] as const))), [states.data]);
  if (full) return { byStation, tiles: byStation };
  return { byStation: EMPTY, tiles: simple ? simpleTiles : EMPTY_TILES };
}
